import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeContext from '../../../ThemeContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../../services/api';
import ReportPieChart from './ReportPieChart';

const FinancialReport = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
 const queryParams = new URLSearchParams(location.search);
const currentSub = queryParams.get("sub");
const reportOptions = [
  { key: 'user', label: 'User Report' },
  { key: 'commission', label: 'Commission Report' },
  { key: 'usage', label: 'Usage Report' },
].filter(item => item.key !== currentSub);
  // Role & base path detection
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const role = (userInfo.role || 'reseller').toLowerCase();

  const navItems = {
    superadmin: ['resellers', 'clients', 'reports', 'notify', 'admins', 'account'],
    admin: ['resellers', 'clients', 'reports', 'account'],
    reseller: ['clients', 'reports', 'account']
  };

  const tabs = navItems[role] || navItems.reseller;

  const basePath = role === 'superadmin'
    ? '/superadmin'
    : role === 'admin'
      ? '/admin'
      : `/${userInfo.username || 'reseller'}`;

  // Typography
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);


  const [reportView, setReportView] = useState("report");
const [financialData, setFinancialData] = useState([]);
const [loadingFinancial, setLoadingFinancial] = useState(false);
  // Date range
  const today = new Date();
  const currentYear = today.getFullYear();
  const pad = (n) => String(n).padStart(2, '0');

  const defaultStart = `${currentYear}-01-01`;
  const defaultEnd = `${currentYear}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const parseDateSafely = (val) => {
    try {
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const handleUpdateRange = () => {
    const s = parseDateSafely(startDate);
    const e = parseDateSafely(endDate);

    if (!s || !e) {
      toast.error('Invalid date range.');
      return;
    }
    if (e < s) {
      toast.error('End date cannot be before start date.');
      return;
    }

    toast.success('Date range updated.');
    // Here you would typically trigger data fetch with new range
  };

  const isActive = (tab) => location.pathname.includes(`/${tab}`);

  const handleTabClick = (tab) => {
    if (tab === 'clients') {
      navigate(`${basePath}/clients`);
    } else if (tab === 'reports') {
      navigate(`${basePath}/reports?sub=financial`); // or whatever sub param you use
    } else {
      navigate(`${basePath}/${tab}`);
    }
  };

  const handleClientSubTab = (subTab) => {
    const path = subTab === 'enterprise' ? 'enterprise-clients' : 'clients';
    navigate(`${basePath}/${path}`);
    setIsClientsDropdownOpen(false);
  };

  const handleReportSubTab = (subTabKey) => {
    navigate(`${basePath}/reports?sub=${subTabKey}`);
    setIsReportsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target)) {
        setIsClientsDropdownOpen(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Example data fetch (replace with your real logic)
  useEffect(() => {
  const fetchFinancialSummary = async () => {
    setLoadingFinancial(true);
    try {
      const token = localStorage.getItem('token');

      const response = await api.get('/financial/summary', {
        headers: { Authorization: `Bearer ${token}` },
        params: { start: startDate, end: endDate }
      });

      // IMPORTANT: store properly for pie chart
      setFinancialData(response.data || []);

    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate('/login');
      } else if (err?.response?.status === 403) {
        toast.error('Unauthorized');
      } else {
        toast.error('Failed to load financial summary.');
      }
    } finally {
      setLoadingFinancial(false);
    }
  };

  fetchFinancialSummary();
}, [startDate, endDate, navigate]);

  

  const actionButtonStyle = {
    background: theme.gradients?.secondary || theme.colors.secondary,
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '12px',
    cursor: 'pointer',
    minWidth: '100px',
    height: '32px',
    fontFamily,
  };

  const dateInputStyle = {
    padding: '6px 10px',
    borderRadius: '999px',
    border: '1px solid #d0d7de',
    fontSize: '12px',
    height: '32px',
    minWidth: '150px',
    outline: 'none',
    fontFamily,
  };

  return (
    <div style={{
      fontFamily,
      backgroundColor: theme.colors.bgPrimary,
      minHeight: '100vh',
      paddingBottom: '3rem',
    }}>
      {/* TOP NAVIGATION TABS */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}>
        {tabs.map(tab => {
          const isActiveTab = isActive(tab);
          const label = tab === 'account' ? 'My Account' : tab === 'notify' ? 'Notify User' : tab.charAt(0).toUpperCase() + tab.slice(1);
          const iconClass =
            tab === 'resellers' ? 'briefcase-fill' :
            tab === 'clients' ? 'people-fill' :
            tab === 'reports' ? 'file-earmark-bar-graph-fill' :
            tab === 'notify' ? 'bell-fill' :
            tab === 'admins' ? 'buildings-fill' :
            'person-lines-fill';

          const isClientsTab = tab === 'clients' && role !== 'reseller';
          const isReportsTab = tab === 'reports';

          return (
            <div
              key={tab}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
              ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
              onMouseEnter={() => {
                if (isClientsTab) setIsClientsDropdownOpen(true);
                if (isReportsTab) setIsReportsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (isClientsTab) setIsClientsDropdownOpen(false);
                if (isReportsTab) setIsReportsDropdownOpen(false);
              }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  background: isActiveTab ? theme.gradients?.primary || theme.colors.primary : theme.gradients?.secondary || theme.colors.secondary,
                  color: '#fff',
                  cursor: isClientsTab || isReportsTab ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
                onClick={() => !(isClientsTab || isReportsTab) && handleTabClick(tab)}
              >
                <i className={`bi bi-${iconClass}`} style={{ fontSize: '1rem' }}></i>
                {label}
              </button>

              {/* Clients Dropdown - only for superadmin/admin */}
              {isClientsTab && isClientsDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '6px',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  zIndex: 30,
                  width: '100%',
                  minWidth: '160px',
                  overflow: 'hidden',
                  fontFamily
                }}>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('core')}
                    onMouseOver={e => e.target.style.background = theme.gradients?.secondary || theme.colors.secondary}
                    onMouseOut={e => e.target.style.background = 'transparent'}
                  >{displayText(" Court Client ")}</button>
                  <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('enterprise')}
                    onMouseOver={e => e.target.style.background = theme.gradients?.secondary || theme.colors.secondary}
                    onMouseOut={e => e.target.style.background = 'transparent'}
                  >{displayText(" Enterprise Client ")}</button>
                </div>
              )}

              {/* Reports Dropdown */}
              {isReportsTab && isReportsDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '6px',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  zIndex: 30,
                  width: '100%',
                  minWidth: '180px',
                  overflow: 'hidden',
                  fontFamily
                }}>
                  {reportOptions.map((item, idx, arr) => ( 
                    <React.Fragment key={item.key}>
                      <button
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          background: item.key === 'financial' ? (theme.gradients?.primary || theme.colors.primary) : 'transparent',
                          color: item.key === 'financial' ? '#fff' : theme.colors.textPrimary,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontFamily
                        }}
                        onClick={() => handleReportSubTab(item.key)}
                        onMouseOver={e => item.key !== 'financial' && (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                        onMouseOut={e => item.key !== 'financial' && (e.target.style.background = 'transparent')}
                      >
                        {item.label}
                      </button>
                      {idx < arr.length - 1 && <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BREADCRUMB */}
      <div style={{
        padding: '0 0.75rem',
        marginBottom: '0.75rem'
      }}>
        <span
          style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
            color: theme.colors.primary
          }}
          onClick={() => navigate(`${basePath}/reports?sub=financial`)}
        >
          Financial Report
        </span>
        <span style={{ margin: '0 4px' }}>/</span>
        <span style={{ color: theme.colors.textPrimary }}>Report</span>
      </div>

      {/* TOOLBAR: Export + Date Range */}
      <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.25rem',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '0 0.75rem'
}}>
  <button style={actionButtonStyle} onClick={() => toast.info('Export not implemented yet')}>
    Export
  </button>

  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
    
    {/* Start Date */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontWeight: 500, fontSize: '12px' }}>Start:</span>
      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        style={dateInputStyle}
      />
    </div>

    {/* End Date */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontWeight: 500, fontSize: '12px' }}>End:</span>
      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        style={dateInputStyle}
      />
    </div>

    {/* Update Button */}
    <button
      onClick={handleUpdateRange}
      style={{ ...actionButtonStyle, background: theme.gradients?.primary || theme.colors.primary }}
    >
      Update
    </button>

    {/* DROPDOWN JUST AFTER UPDATE */}
    <select
      value={reportView}
      onChange={(e) => {
  const value = e.target.value;
  setReportView(value);
}}
    >
      <option value="report">Report View</option>
      <option value="pie-chart">Pie Chart</option>
    </select>

  </div>
</div>
      

      {/* MAIN CONTENT - Financial Summary */}
        {reportView === "pie-chart" ? (
  <ReportPieChart
    reportType="financial"
    rawData={financialData}
    isLoading={loadingFinancial}
  />
) : loadingFinancial ? (
  <div style={{ textAlign: 'center', padding: '2rem' }}>
    Loading financial data...
  </div>
) : (
  <>
    {/* YOUR EXISTING REPORT UI */}
    <div style={{
      fontSize: `${bodyFontSize + 4}px`,
      fontWeight: 'bold',
      marginBottom: '1.5rem',
      color: theme.colors.textPrimary
    }}>
      Financial Summary
    </div>

    <div style={{
      background: '#f8f9fa',
      padding: '1.25rem',
      borderRadius: '8px',
      border: `1px solid ${theme.colors.borderColor}`
    }}>
      <div style={{
        fontSize: `${bodyFontSize + 2}px`,
        fontWeight: '600',
        marginBottom: '0.75rem'
      }}>
        Total Charge: <span style={{ color: theme.colors.primary }}>$0.00</span>
      </div>
      <p style={{ color: '#555', margin: 0 }}>
        Date range: {startDate} to {endDate}
      </p>
    </div>
  </>
)}

        
      </div>
  );
};

export default FinancialReport;